'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';

const navLinks = [
    { label: 'Home', href: '/' },
    { label: 'Products', href: '/products' },
    { label: 'Contact', href: '/contact' },
];

export default function Header() {
    const [scrolled, setScrolled] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const pathname = usePathname();

    useEffect(() => {
        const handleScroll = () => setScrolled(window.scrollY > 40);
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        if (mobileOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }

        return () => {
            document.body.style.overflow = '';
        };
    }, [mobileOpen]);

    const isActive = (href: string) => {
        if (href === '/') {
            return pathname === '/';
        }

        return pathname === href || pathname.startsWith(`${href}/`);
    };

    return (
        <>
            <nav
                className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${scrolled ? 'py-3' : 'py-5'
                    }`}
            >
                <div className="max-w-7xl mx-auto px-6 md:px-10">
                    <div
                        className={`flex items-center justify-between rounded-2xl px-5 py-3 transition-all duration-500 ${scrolled ? 'glass-nav shadow-lg shadow-black/30' : ''
                            }`}
                    >
                        {/* Logo */}
                        <Link href="/" className="flex items-center gap-1.5">
                            <AppLogo size={22} />
                            <span className="font-display font-800 text-xl tracking-tight text-foreground">
                                Codigo
                            </span>
                        </Link>

                        {/* Desktop Nav */}
                        <div className="hidden md:flex items-center gap-1 glass-nav rounded-full px-2 py-1.5">
                            {navLinks.map((link) => {
                                const active = isActive(link.href);

                                return (
                                    <Link
                                        key={link.href}
                                        href={link.href}
                                        className={`px-5 py-2 rounded-full text-xs font-700 uppercase tracking-widest transition-all duration-200 ${active
                                                ? 'bg-primary text-white'
                                                : 'text-muted-foreground hover:text-foreground hover:bg-white/5'
                                            }`}
                                    >
                                        {link.label}
                                    </Link>
                                );
                            })}
                        </div>

                        {/* CTA */}
                        <div className="hidden md:flex items-center gap-3">
                            <Link
                                href="/contact"
                                className="btn-primary text-sm py-2.5 px-5"
                            >
                                Work with us
                            </Link>
                        </div>

                        {/* Mobile Hamburger */}
                        <button
                            onClick={() => setMobileOpen(true)}
                            className="md:hidden p-2.5 glass-nav rounded-xl text-foreground"
                            aria-label="Open menu"
                        >
                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                <path
                                    d="M3 5h14M3 10h14M3 15h14"
                                    stroke="currentColor"
                                    strokeWidth="1.75"
                                    strokeLinecap="round"
                                />
                            </svg>
                        </button>
                    </div>
                </div>
            </nav>

            {/* Mobile Menu Overlay */}
            {mobileOpen && (
                <div
                    className="fixed inset-0 z-[100] flex flex-col"
                    style={{
                        background: 'rgba(10,11,15,0.97)',
                        backdropFilter: 'blur(20px)',
                    }}
                >
                    <div className="flex items-center justify-between px-6 py-5">
                        <Link
                            href="/"
                            className="flex items-center gap-2.5"
                            onClick={() => setMobileOpen(false)}
                        >
                            <AppLogo size={32} />
                            <span className="font-display font-800 text-xl tracking-tight text-foreground">
                                Codigo
                            </span>
                        </Link>

                        <button
                            onClick={() => setMobileOpen(false)}
                            className="p-2.5 glass-nav rounded-xl text-foreground"
                            aria-label="Close menu"
                        >
                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                <path
                                    d="M5 5l10 10M15 5L5 15"
                                    stroke="currentColor"
                                    strokeWidth="1.75"
                                    strokeLinecap="round"
                                />
                            </svg>
                        </button>
                    </div>

                    <div className="flex flex-col gap-2 px-6 mt-8">
                        {navLinks.map((link) => {
                            const active = isActive(link.href);

                            return (
                                <Link
                                    key={link.href}
                                    href={link.href}
                                    onClick={() => setMobileOpen(false)}
                                    className={`text-2xl font-700 tracking-tight py-4 border-b border-border transition-colors ${active
                                            ? 'text-primary'
                                            : 'text-foreground hover:text-primary'
                                        }`}
                                >
                                    {link.label}
                                </Link>
                            );
                        })}
                    </div>

                    <div className="px-6 mt-8">
                        <Link
                            href="/contact"
                            onClick={() => setMobileOpen(false)}
                            className="btn-primary w-full text-center justify-center"
                        >
                            Work with Codigo
                        </Link>
                    </div>
                </div>
            )}
        </>
    );
}